/**
 * ============================================================================
 * GLOSSARY TAB — last tab in the tab bar.
 * ============================================================================
 *
 *
 * Thin composition of shared primitives (the pattern all tabs should follow):
 *   QueryStateGuard (loading/error) → SectionCard (chrome) → DataTable (grid)
 * All this file owns is: which query to run, column configs, and layout order.
 *
 * TABLE BEHAVIOUR (matches the legacy dashboard):
 * - singleLine: every row is one line; overflowing text gets an ellipsis and
 *   the full text shows in a native tooltip on hover.
 * - maxVisibleRows=20: at most 20 rows visible; more scroll within the table
 *   under a sticky header. Shorter tables size to their content.
 *
 * DATA: GET /executive-summary/glossary via useGetGlossaryQuery — currently
 * mocked in executiveSummaryApi (see TODO there to switch to the real API).
 */

import React from 'react';
import {
  DataTable,
  DataTableColumn,
  ExportColumn,
  ExportMenu,
  QueryStateGuard,
  SectionCard,
} from 'src/components/common';
import { useGetGlossaryQuery } from 'src/features/api/executiveSummaryApi';
import { GlossaryTerm, MetricDefinition } from 'src/types';

const MAX_VISIBLE_ROWS = 12;

/**
 * px widths (not %) intentionally: their sum exceeds the container, so the
 * table scrolls horizontally inside its own wrapper — the page itself never
 * scrolls sideways. Example/Description get generous widths so more text is
 * readable before the ellipsis kicks in.
 */
const metricDefinitionColumns: Array<DataTableColumn<MetricDefinition>> = [
  { id: 'order', header: '#', width: '50px', render: (m) => m.order },
  { id: 'name', header: 'Metric Name', width: '260px', render: (m) => m.metricName },
  { id: 'basis', header: 'Reporting Basis', width: '230px', render: (m) => m.reportingBasis },
  { id: 'definition', header: 'Definition', width: '520px', render: (m) => m.definition },
  { id: 'example', header: 'Example', width: '700px', render: (m) => m.example },
];

const glossaryTermColumns: Array<DataTableColumn<GlossaryTerm>> = [
  { id: 'category', header: 'Category', width: '200px', render: (t) => t.category },
  { id: 'term', header: 'Term', width: '340px', render: (t) => t.term },
  { id: 'description', header: 'Description', width: '900px', render: (t) => t.description },
];

/** Export variants: raw text per cell (see exportTable.ts contract). */
const metricDefinitionExportColumns: Array<ExportColumn<MetricDefinition>> = [
  { header: '#', value: (m) => m.order },
  { header: 'Metric Name', value: (m) => m.metricName },
  { header: 'Reporting Basis', value: (m) => m.reportingBasis },
  { header: 'Definition', value: (m) => m.definition },
  { header: 'Example', value: (m) => m.example },
];

const glossaryTermExportColumns: Array<ExportColumn<GlossaryTerm>> = [
  { header: 'Category', value: (t) => t.category },
  { header: 'Term', value: (t) => t.term },
  { header: 'Description', value: (t) => t.description },
];

export const GlossaryTab: React.FC = () => {
  const query = useGetGlossaryQuery();

  return (
    <QueryStateGuard query={query} errorText="Failed to load glossary data. Please try again later.">
      {(data) => (
        <div>
          <SectionCard
            title="Metric Definitions"
            actions={
              <ExportMenu
                columns={metricDefinitionExportColumns}
                rows={data.metricDefinitions}
                filename="metric-definitions"
              />
            }
          >
            <DataTable
              columns={metricDefinitionColumns}
              items={data.metricDefinitions}
              rowKey={(m) => String(m.order)}
              headerTheme="blue"
              singleLine
              maxVisibleRows={MAX_VISIBLE_ROWS}
            />
          </SectionCard>

          <SectionCard
            title="Glossary - Filters, Tabs and Terms"
            actions={
              <ExportMenu
                columns={glossaryTermExportColumns}
                rows={data.terms}
                filename="glossary-terms"
              />
            }
          >
            <DataTable
              columns={glossaryTermColumns}
              items={data.terms}
              rowKey={(t) => `${t.category}|${t.term}`}
              headerTheme="navy"
              singleLine
              maxVisibleRows={MAX_VISIBLE_ROWS}
            />
          </SectionCard>
        </div>
      )}
    </QueryStateGuard>
  );
};
